import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { finalize } from 'rxjs/operators';

import { ADDON_PENDO_EVENTS } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
import { FeatureInfo } from 'EntitlementsApp/ts/interfaces/feature-info.interface';
import { FeatureNeededComponentState } from 'EntitlementsApp/ts/interfaces/feature-needed-component-state.interface';

import { YChartsSentryUtils } from 'Angular/services/sentry/sentry-utils';
import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';

/**
 *
 * Feature Needed Component
 *
 * Modal indicating to the user that they do not have the required feature to support certain functionality.
 *
 */

@Component({
    standalone: false,
    selector: 'ycn-feature-needed',
    templateUrl: './feature-needed.template.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeatureNeededComponent {
    state: FeatureNeededComponentState;

    private _feature: string | null = null;

    constructor(private featureService: FeatureService, private changeDetector: ChangeDetectorRef) {
        this.state = {
            featureInfo: null,
            loading: true,
            modalOpen: false,
            error: false,
        };
    }

    openModal(feature: string): void {
        /**
         * The caller of `openModal` should check whether the use has the feature before opening
         * the modal.
         */
        this._feature = feature;
        this.state.loading = true;
        this.state.error = false;

        this.featureService
            .getFeature(feature, window.location.pathname)
            .pipe(
                // Finalize gets called after both `next` and after `error`
                finalize(() => {
                    this.state.loading = false;
                    this.state.modalOpen = true;
                    this.changeDetector.markForCheck();
                })
            )
            .subscribe({
                next: (data: FeatureInfo) => {
                    this.state.featureInfo = data;
                },
                error: (e) => {
                    // The only way there could be an error is if it is a bug or network error, alert sentry here
                    this.state.error = true;
                    YChartsSentryUtils.captureException(e);
                },
            });
    }

    protected onCallToAction(): void {
        // Only add-on CTAs ("Add to My Account") of mapped features are tracked
        if (!this.state.featureInfo?.is_addon) return;

        const event = this._feature ? ADDON_PENDO_EVENTS[this._feature] : undefined;
        if (!event) return;

        window.pendoTrack(event, {});
    }

    closeModal(): void {
        this.state.modalOpen = false;
        this.changeDetector.markForCheck();
    }
}
